import { round2 } from '../tabs/forecasting/forecastingModel';
import { INCLUSION_TYPE_OPTIONS } from '../tabs/inclusions/fields';

import {
  blankedErrorNote,
  blankedPriceNote,
  cellAddress,
  cellAt,
  findImportColumns,
  findLabel,
  positiveNumberOf,
  priceOf,
  textOf,
  warnAt,
} from './sheetCells';
import type { ParsedInclusion, SheetContext, SheetRows } from './types';

const COLUMNS = {
  name: 'Asset Name',
  type: 'Type',
  description: 'Inclusion Description',
  prv: 'PRV',
  rrp: 'RRP',
} as const;

// The template fills untyped cells with formulas that show 0.
function typedTextOf(value: unknown): string | null {
  const text = textOf(value)?.replace(/\s+/g, ' ');
  return text && text !== '0' ? text : null;
}

function typeOf(value: unknown): string | null {
  const text = typedTextOf(value)?.toLowerCase();
  return INCLUSION_TYPE_OPTIONS.find((type) => type.toLowerCase() === text) ?? null;
}

// The Inclusions Import sheet copies the AUD RRP column of OTA Rates.
export const WORKBOOK_RRP_CURRENCY = 'AUD';

interface LocalRrps {
  currency: string;
  byName: Record<string, Array<number | null>>;
}

// OTA Rates is where the PCM types RRP, in the workbook's currency, labelled on the row above the header.
export function parseLocalRrps(rows: SheetRows): LocalRrps | null {
  const header = findLabel(rows, 'Inclusion');
  if (!header) {
    return null;
  }
  const currencyOf = (col: number) => textOf(cellAt(rows, header.row - 1, col))?.toUpperCase();
  const rrpCol = (rows[header.row] ?? []).findIndex((value, col) => {
    const currency = currencyOf(col);
    return textOf(value)?.toLowerCase() === 'rrp' && !!currency && currency !== WORKBOOK_RRP_CURRENCY;
  });
  const currency = rrpCol === -1 ? undefined : currencyOf(rrpCol);
  if (!currency) {
    return null;
  }

  const byName: LocalRrps['byName'] = {};
  for (let row = header.row + 1; row < rows.length; row++) {
    const name = typedTextOf(cellAt(rows, row, header.col));
    if (name) {
      const rrp = positiveNumberOf(cellAt(rows, row, rrpCol));
      (byName[name] ??= []).push(rrp === null ? null : round2(rrp));
    }
  }
  return { currency, byName };
}

// Matched by name, not row: the two sheets only line up while nobody inserts a row. Repeated names pair in order.
export function withLocalRrps(inclusions: Array<ParsedInclusion>, byName: LocalRrps['byName']): Array<ParsedInclusion> {
  const used: Record<string, number> = {};
  return inclusions.map((inclusion) => {
    const index = used[inclusion.name] ?? 0;
    used[inclusion.name] = index + 1;
    const amounts = byName[inclusion.name];
    return amounts && index < amounts.length ? { ...inclusion, localRrp: amounts[index] } : inclusion;
  });
}

export function parseInclusions(context: SheetContext): Array<ParsedInclusion> {
  const columns = findImportColumns(context, COLUMNS, 'inclusions', 'inclusions');
  if (!columns) {
    return [];
  }

  const inclusions: Array<ParsedInclusion> = [];
  for (let row = 1; row < context.rows.length; row++) {
    const cell = (column: keyof typeof COLUMNS) => cellAt(context.rows, row, columns[column]);
    const name = typedTextOf(cell('name'));
    const prv = priceOf(cell('prv'));
    // RRP goes to Salesforce and its field takes cents, unlike PRV, which the tool shows in whole dollars.
    const rrp = positiveNumberOf(cell('rrp'));
    const reject = (message: string) =>
      context.issues.push({
        section: 'inclusions',
        severity: 'error',
        cell: cellAddress(row, columns.name),
        row: row + 1,
        message,
      });

    if (!name) {
      if (prv !== null || rrp !== null) {
        reject('the Asset Name is empty. Name the inclusion in the workbook and upload again.');
      }
      continue;
    }
    if (name.startsWith('#')) {
      reject(`the Asset Name shows ${name.replace(/^#ERROR_/, '')}. Fix the workbook and upload again.`);
      continue;
    }

    const where = (column: keyof typeof COLUMNS) => ({ column: COLUMNS[column], row: row + 1, tab: 'Inclusions' });
    const warn = (column: keyof typeof COLUMNS, note: string | null) =>
      warnAt(context, 'inclusions', row, columns[column], note);
    warn('prv', blankedPriceNote(cell('prv'), prv, where('prv')));
    warn('rrp', blankedPriceNote(cell('rrp'), rrp, where('rrp')));
    const descriptionBlanked = warn('description', blankedErrorNote(cell('description'), where('description')));
    inclusions.push({
      row: row + 1,
      name,
      type: typeOf(cell('type')),
      description: descriptionBlanked ? null : typedTextOf(cell('description')),
      prv,
      rrp: rrp === null ? null : round2(rrp),
    });
  }
  return inclusions;
}
